import api from './api';
import { unwrapList, unwrapObject, type ListResult } from './api-envelope';

export interface ProductExportAttrColumn {
  code: string;
  name: string;
  data_type: string;
  enumLabels?: Record<string, string>;
}

export interface ProductExportData {
  products: Array<Record<string, unknown>>;
  variantsByProduct: Record<string, Array<Record<string, unknown>>>;
  attrColumns: ProductExportAttrColumn[];
}
import type { ProductListQuery } from './queryKeys';
import type {
  ProductTenantType,
  CreateProductInput,
  UpdateProductInput,
  VariantUpsertInput,
} from '@tcr-erp/shared';
import { ProductState } from '@tcr-erp/shared';

export interface VariantSearchResult {
  _id: string;
  variant_code: string;
  product_id: string;
  product_name: string;
  product_sku: string;
  tracking_mode: 'quantity' | 'serial';
  default_inventory_account_code?: string;
  attrs: Record<string, unknown>;
  is_default: boolean;
  is_sellable: boolean;
  is_published: boolean;
  unit_price?: number;
  cached_price?: number;
  weight_g?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
}

export interface VariantSearchResponse {
  data: VariantSearchResult[];
  total: number;
}

// Strip fields that the backend CreateVariantDto rejects via whitelist validation:
// - cached_price / cached_buy_price / cached_price_at: FE-only preview fields
// - product_id / tenant_id / created_at / updated_at / __v / cached_buy_price_at:
//   server-managed Mongoose document metadata leaked from GET responses
//   (relevant when editing/cloning where variants are loaded from BE)
// Logistics fields are valid API fields and must be preserved explicitly.
function sanitizeVariant(v: VariantUpsertInput, opts?: { keepId?: boolean }): VariantUpsertInput {
  const {
    weight_g,
    length_cm,
    width_cm,
    height_cm,
    cached_price: _cp,
    cached_buy_price: _cbp,
    cached_price_at: _cpa,
    cached_buy_price_at: _cbpa,
    product_id: _pid,
    tenant_id: _tid,
    created_at: _ca,
    updated_at: _ua,
    __v: _v,
    _id,
    ...clean
  } = v as VariantUpsertInput & {
    cached_price?: unknown;
    cached_buy_price?: unknown;
    cached_price_at?: unknown;
    cached_buy_price_at?: unknown;
    product_id?: unknown;
    tenant_id?: unknown;
    created_at?: unknown;
    updated_at?: unknown;
    __v?: unknown;
    _id?: unknown;
    weight_g?: unknown;
    length_cm?: unknown;
    width_cm?: unknown;
    height_cm?: unknown;
  };
  const logistics = {
    ...(weight_g !== undefined ? { weight_g } : {}),
    ...(length_cm !== undefined ? { length_cm } : {}),
    ...(width_cm !== undefined ? { width_cm } : {}),
    ...(height_cm !== undefined ? { height_cm } : {}),
  };
  // `_id` là metadata server: strip khi TẠO MỚI (kể cả clone — variant mới không được
  // mang _id ngoại lai). Nhưng khi UPDATE phải GIỮ `_id` để BE diff variant theo _id;
  // thiếu _id → BE coi variant đang có là "tạo mới" → 409 DUPLICATE_VARIANT_CODE.
  if (opts?.keepId && _id != null) {
    return { ...clean, ...logistics, _id: String(_id) } as VariantUpsertInput;
  }
  return { ...clean, ...logistics } as VariantUpsertInput;
}

function sanitizeBody<T extends { variants?: VariantUpsertInput[] }>(
  body: T,
  opts?: { keepId?: boolean },
): T {
  if (!body.variants?.length) return body;
  return { ...body, variants: body.variants.map((v) => sanitizeVariant(v, opts)) };
}

/**
 * TCR-2348 — Body `POST /products/import` (nhập SP hàng loạt từ file Salework + AMIS đã ghép).
 * BE giới hạn tối đa 500 dòng/request; FE chia lô 100 dòng (xem ImportSaleworkAmisModal).
 * `dry_run=true` chỉ kiểm tra hợp lệ, không ghi DB.
 */
export interface ImportBomComponent { sku: string; name: string; unit: string; qty: number }
export interface ImportProductRow {
  sku: string; name: string; unit?: string; category?: string; price?: number; barcode?: string; image_url?: string;
  is_combo?: boolean; is_bom_material?: boolean; bom_components?: ImportBomComponent[]; bom_errors?: string[];
}

export interface ImportProductsBody {
  rows: ImportProductRow[];
  /** Tuỳ chọn — bỏ trống thì BE dùng thương hiệu "Không thương hiệu" của tenant. */
  default_brand_id?: string;
  inventory_item_group_id: string;
  /** Nhóm kế toán riêng cho các dòng vật tư suy ra từ BOM. */
  bom_material_inventory_item_group_id?: string;
  default_category_id?: string;
  history_id?: string;
  history_row_offset?: number;
  previously_failed_skus?: string[];
  dry_run?: boolean;
}

export type ProductImportSource = 'salework' | 'amis' | 'salework_amis';
export type ProductImportHistoryStatus = 'uploading' | 'queued' | 'processing' | 'completed' | 'failed';
export interface StartProductImportHistoryInput {
  source: ProductImportSource;
  total_rows: number;
  inventory_item_group_id: string;
  default_brand_id?: string;
  bom_material_inventory_item_group_id?: string;
  default_category_id?: string;
}
export interface StartProductImportHistoryResult { history_id: string; started_at: string }
export interface UploadProductImportHistoryRowsResult { received_rows: number; total_rows: number }
export interface SubmitProductImportHistoryResult {
  history_id: string;
  status: 'queued';
  received_rows: number;
  total_rows: number;
}
export interface ProductImportHistorySummary {
  history_id: string;
  source: ProductImportSource;
  started_by: string;
  started_by_name: string;
  started_at: string;
  completed_at?: string;
  duration_ms: number;
  total_rows: number;
  received_rows: number;
  created_count: number;
  updated_count: number;
  error_count: number;
  skipped_duplicate_count: number;
  failure_reason?: string;
  status: ProductImportHistoryStatus;
}
export interface ProductImportHistoryRow {
  row_index: number;
  sku: string;
  status: 'created' | 'updated' | 'error' | 'skipped_duplicate';
  message?: string;
  product_id?: string;
}
export interface ProductImportTransportError { row_index: number; sku: string; message: string }
export interface ProductImportHistoryPage<T> extends ListResult<T> { page: number; page_size: number }

/** Kết quả từng dòng BE trả về (thứ tự khớp `rows`; `product_id` có thể có khi lưu BOM lỗi sau khi tạo combo). */
export interface ImportRowResult {
  sku: string;
  status: 'created' | 'updated' | 'valid' | 'skipped_duplicate' | 'error';
  product_id?: string;
  message?: string;
}

export const productService = {
  list: (params: ProductListQuery): Promise<ListResult<ProductTenantType>> =>
    api.get('/products', { params }).then(unwrapList<ProductTenantType>),

  getById: (id: string): Promise<ProductTenantType> =>
    api.get(`/products/${id}`).then(unwrapObject<ProductTenantType>),

  create: (body: CreateProductInput): Promise<ProductTenantType> =>
    api.post('/products', sanitizeBody(body)).then(unwrapObject<ProductTenantType>),

  update: (id: string, body: UpdateProductInput): Promise<ProductTenantType> =>
    api.put(`/products/${id}`, sanitizeBody(body, { keepId: true })).then(unwrapObject<ProductTenantType>),

  updateState: (id: string, state: ProductState): Promise<ProductTenantType> =>
    api.patch(`/products/${id}/state`, { state }).then(unwrapObject<ProductTenantType>),

  remove: (id: string): Promise<void> =>
    api.delete(`/products/${id}`).then(() => undefined),

  clone: (id: string): Promise<{ _id: string; name: string }> =>
    api.post(`/products/${id}/clone`).then(unwrapObject<{ _id: string; name: string }>),

  /** Chuyển sản phẩm thường → combo thật (BE gác bằng công tắc ở Cài đặt chung). */
  convertToCombo: (id: string): Promise<{ product_id: string; is_combo: boolean }> =>
    api.post(`/products/${id}/convert-to-combo`).then(unwrapObject<{ product_id: string; is_combo: boolean }>),

  /** Chuyển combo thật → sản phẩm thường. BE chặn khi còn định mức hoặc đã có lệnh lắp ráp. */
  convertToProduct: (id: string): Promise<{ product_id: string; is_combo: boolean }> =>
    api.post(`/products/${id}/convert-to-product`).then(unwrapObject<{ product_id: string; is_combo: boolean }>),

  /**
   * Toàn bộ sản phẩm ACTIVE, gọi nhiều trang rồi ghép lại.
   *
   * BE chặn `page_size > 100` (trả 400 — TCR-1081: ô "Sản phẩm bắt buộc" của
   * RebatePolicyForm từng gửi `page_size: 200` nên dropdown rỗng). Helper này
   * luôn gọi `page_size: 100` và lặp qua `total_pages` để trả đủ danh sách
   * sản phẩm được phép bán cho ô chọn có lọc theo tên ở phía client.
   */
  listAllActive: async (): Promise<ListResult<ProductTenantType>> => {
    const PAGE_SIZE = 100;
    const first = await productService.list({ page: 1, page_size: PAGE_SIZE, state: [ProductState.ACTIVE] });
    const totalPages = Math.max(1, Math.ceil(first.total / PAGE_SIZE));
    const rest = totalPages > 1
      ? await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, index) => (
          productService.list({ page: index + 2, page_size: PAGE_SIZE, state: [ProductState.ACTIVE] })
        )),
      )
      : [];
    return {
      data: [first, ...rest].flatMap((page) => page.data),
      total: first.total,
    };
  },

  createVariant: (productId: string, body: VariantUpsertInput): Promise<{ _id: string }> =>
    api.post(`/products/${productId}/variants`, sanitizeVariant(body)).then(unwrapObject<{ _id: string }>),

  updateVariant: (productId: string, variantId: string, body: VariantUpsertInput): Promise<unknown> =>
    api.put(`/products/${productId}/variants/${variantId}`, sanitizeVariant(body)).then((r) => r.data),

  bulkUpdateState: (ids: string[], state: ProductState): Promise<{ updated: number }> =>
    api.patch('/products/bulk-state', { ids, state }).then(unwrapObject<{ updated: number }>),

  /** TCR-2484: replace = xoá danh mục cũ rồi gán mới; append = giữ cũ, thêm mới. `updated` không tính SP đã đúng danh mục. */
  bulkUpdateCategory: (body: { ids: string[]; category_ids: string[]; mode: 'replace' | 'append' }): Promise<{ matched: number; updated: number }> =>
    api.patch('/products/bulk-category', body).then(unwrapObject<{ matched: number; updated: number }>),

  /** TCR-2348: nhập SP hàng loạt; envelope `{data: ImportRowResult[]}` → unwrapObject = res.data.data. */
  importProducts: (body: ImportProductsBody): Promise<ImportRowResult[]> =>
    api.post('/products/import', body).then(unwrapObject<ImportRowResult[]>),

  startImportHistory: (body: StartProductImportHistoryInput): Promise<StartProductImportHistoryResult> =>
    api.post('/products/import-history', body).then(unwrapObject<StartProductImportHistoryResult>),

  uploadImportHistoryRows: (historyId: string, body: { start_index: number; rows: ImportProductRow[] }): Promise<UploadProductImportHistoryRowsResult> =>
    api.post(`/products/import-history/${historyId}/input-rows`, body).then(unwrapObject<UploadProductImportHistoryRowsResult>),

  submitImportHistory: (historyId: string): Promise<SubmitProductImportHistoryResult> =>
    api.post(`/products/import-history/${historyId}/submit`).then(unwrapObject<SubmitProductImportHistoryResult>),

  abortImportHistoryUpload: (historyId: string): Promise<ProductImportHistorySummary> =>
    api.post(`/products/import-history/${historyId}/abort-upload`).then(unwrapObject<ProductImportHistorySummary>),

  recordImportHistoryTransportErrors: (historyId: string, rows: ProductImportTransportError[]): Promise<void> =>
    api.post(`/products/import-history/${historyId}/rows`, { rows }).then(() => undefined),

  getImportHistory: (historyId: string): Promise<ProductImportHistorySummary> =>
    api.get(`/products/import-history/${historyId}`).then(unwrapObject<ProductImportHistorySummary>),

  listImportHistory: (params: { page?: number; page_size?: number; active_only?: boolean; mine?: boolean }): Promise<ProductImportHistoryPage<ProductImportHistorySummary>> =>
    api.get('/products/import-history', { params }).then((response) => ({
      ...unwrapList<ProductImportHistorySummary>(response),
      page: response.data.pagination?.page ?? params.page ?? 1,
      page_size: response.data.pagination?.page_size ?? params.page_size ?? 20,
    })),

  listImportHistoryRows: (historyId: string, params: { page?: number; page_size?: number; status?: ProductImportHistoryRow['status'] }): Promise<ProductImportHistoryPage<ProductImportHistoryRow>> =>
    api.get(`/products/import-history/${historyId}/rows`, { params }).then((response) => ({
      ...unwrapList<ProductImportHistoryRow>(response),
      page: response.data.pagination?.page ?? params.page ?? 1,
      page_size: response.data.pagination?.page_size ?? params.page_size ?? 20,
    })),

  exportList: (params: Omit<ProductListQuery, 'page' | 'page_size'>): Promise<ProductExportData> =>
    api.get('/products/export', { params }).then(unwrapObject<ProductExportData>),

  variantSearch: (params: {
    q?: string;
    limit?: number;
    include_hidden?: boolean;
    brand_id?: string;
    /** true = chỉ variant enable bán trên POS. */
    is_sellable_pos?: boolean;
  }): Promise<VariantSearchResponse> =>
    api.get('/variants/search', { params }).then((r) => r.data),

  variantsByIds: (ids: string[]): Promise<VariantSearchResult[]> => {
    if (!ids.length) return Promise.resolve([]);
    const batches: string[][] = [];
    for (let index = 0; index < ids.length; index += 50) {
      batches.push(ids.slice(index, index + 50));
    }
    return Promise.all(
      batches.map((batch) =>
        api
          .get<{ data?: VariantSearchResult[] } | VariantSearchResult[]>(
            '/variants/by-ids',
            { params: { ids: batch.join(',') } },
          )
          .then((r) => {
            const payload = r.data;
            return Array.isArray(payload) ? payload : payload.data ?? [];
          }),
      ),
    ).then((results) => results.flat());
  },
};
